import { lazy } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'
import { isChunkLoadError, reloadForNewBuild } from '../lib/pwa/chunk-reload.ts'

/**
 * `React.lazy` for a page module that uses a named export.
 *
 * ```ts
 * const TagsPage = lazyPage(() => import('../pages/TagsPage.tsx'), 'TagsPage')
 * ```
 *
 * If the chunk is gone because a new version was deployed, the page reloads
 * once (see chunk-reload.ts). The promise then never settles, so the Suspense
 * fallback stays on screen until the reload instead of the error boundary
 * flashing.
 */
export function lazyPage<K extends string, M extends Record<K, ComponentType>>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K]> {
  return lazy(async () => {
    try {
      const module = await load()
      return { default: module[name] }
    } catch (err) {
      if (isChunkLoadError(err) && reloadForNewBuild()) return new Promise<never>(() => {})
      throw err
    }
  })
}
